import jsPDF from "jspdf";
import { FaPrint } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import AuthContext from "../../../../store/auth-context";
import { getHeaderHtml, parseHeader, drawReportHeader } from "../../../../util/headerUtil";
import { themeColors } from "../../../../util/themeColors";

export const printDischarge = (formData, authCtx, paperSize = "a4") => {
  const hospitalData = authCtx?.hospitalData;
  const printWindow = window.open("", "_blank", "width=900,height=650");
  if (!printWindow) return;

  const isA5 = paperSize === "a5";

  printWindow.document.write(`
    <html>
      <head>
        <title>Discharge Summary</title>
        <style>
          :root {
            --primary-color: ${themeColors[hospitalData?.applicationThemeColour || 'TEAL']?.primary || '#006d77'};
            --primary-rgb: ${(() => {
      const hex = themeColors[hospitalData?.applicationThemeColour || 'TEAL']?.primary || '#006d77';
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `${r}, ${g}, ${b}`;
    })()};
          }
          @page { margin: ${isA5 ? '5mm' : '10mm'}; size: ${paperSize}; }
          @media print {
            body { -webkit-print-color-adjust: exact; margin: 0; padding: 0; }
            html, body { height: 100%; }
          }
          body { 
            font-family: 'Segoe UI', Arial, sans-serif; 
            margin: 0; 
            padding: 0; 
            font-size: ${isA5 ? '13px' : '15px'}; 
            color: #111; 
            line-height: 1.35;
          }
          .print-wrapper {
            display: flex;
            flex-direction: column;
            min-height: 97vh;
            padding: 10px;
            box-sizing: border-box;
          }
          hr { border: 0; border-top: 1px solid #ccc; margin: 5px 0; }

          .title { text-align: center; font-size: ${isA5 ? '18px' : '22px'}; font-weight: bold; margin: 10px 0; color: #000; text-transform: uppercase; border-bottom: 1px solid #ccc; padding-bottom: 5px; }
          .row { display: flex; flex-wrap: wrap; justify-content: space-between; margin: 4px 0; }
          .row span { flex: 1; padding: 1px 0; }

          .field { margin: 6px 0; }
          .label { font-weight: bold; color: #000; margin-bottom: 2px; }
          .box { border: 1px solid #cbd5e1; padding: 6px 10px; min-height: 25px; margin: 4px 0; border-radius: 4px; background-color: #fcfcfc; }
          
          .summary-section { margin-top: 15px; border-top: 2px solid var(--primary-color); padding-top: 8px; }
          .summary-label { font-size: ${isA5 ? '15px' : '17px'}; font-weight: bold; color: var(--primary-color); margin-bottom: 8px; display: block; text-transform: uppercase; }
          .summary-content { line-height: 1.5; text-align: justify; color: #222; flex-grow: 1; }
          .summary-content * { margin-bottom: 6px; }

          .footer { 
            margin-top: auto; 
            display: flex; 
            justify-content: space-between; 
            page-break-inside: avoid; 
            padding-top: 30px;
            padding-bottom: 10px;
            border-top: 1px transparent; /* Ensures it stays as a block */
          }
          .sign-box { text-align: center; width: 45%; }
          .sign-line { border-top: 1.5px solid #333; padding-top: 6px; font-weight: bold; text-transform: uppercase; font-size: 13px; }

          .info-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          .info-table td { padding: 3px 0; vertical-align: top; }
        </style>
      </head>
      <body>
        <div class="print-wrapper">
          ${getHeaderHtml(hospitalData)}

          <div class="title">Discharge Summary</div>

          <table class="info-table">
            <tr>
              <td width="33%">${formData.patientName ? `<strong>Patient Name:</strong> ${formData.patientName}` : ""}</td>
              <td width="33%">${formData.regNo && formData.regNo !== "-" ? `<strong>Reg. No:</strong> ${formData.regNo}` : ""}</td>
              <td width="33%" align="right">${formData.ipdNo && formData.ipdNo !== "-" ? `<strong>IPD No:</strong> ${formData.ipdNo}` : ""}</td>
            </tr>
            <tr>
              <td>${formData.age || formData.sex ? `<strong>Age/Sex:</strong> ${formData.age || "-"}/${formData.sex || "-"}` : ""}</td>
              <td>${formData.weight && formData.weight !== "-" ? `<strong>Weight:</strong> ${formData.weight} kg` : ""}</td>
              <td align="right"><strong>Date:</strong> ${formData.dod || new Date().toLocaleDateString()}</td>
            </tr>
            <tr>
               <td colspan="2">${formData.address && formData.address !== "-" ? `<strong>Address:</strong> ${formData.address}` : ""}</td>
               <td align="right">${formData.doa && formData.doa !== "-" ? `<strong>DOA:</strong> ${formData.doa}` : ""}</td>
            </tr>
            <tr>
               <td>${formData.ids?.adhar && formData.ids?.adhar !== "-" ? `<strong>Adhar No:</strong> ${formData.ids.adhar}` : ""}</td>
               <td>${formData.ids?.pan && formData.ids?.pan !== "-" ? `<strong>PAN No:</strong> ${formData.ids.pan}` : ""}</td>
               <td align="right">${formData.dod && formData.dod !== "-" ? `<strong>DOD:</strong> ${formData.dod}` : ""}</td>
            </tr>
          </table>

          <div class="row" style="gap: 15px; align-items: flex-start;">
            ${(formData.medicoLegal && formData.medicoLegal !== "-") ? `<div style="flex: 1;">
              <div class="label">Medico-Legal / Notes:</div>
              <div class="box">${formData.medicoLegal}</div>
            </div>` : ""}
            ${(formData.diagnosis && formData.diagnosis !== "-") ? `<div style="flex: 1;">
              <div class="label">Diagnosis:</div>
              <div class="box">${formData.diagnosis}</div>
            </div>` : ""}
          </div>

          <div class="row" style="margin-top: 12px; border-top: 1px dotted #ccc; padding-top: 8px;">
            ${formData.dischargeType && formData.dischargeType !== "-" ? `<span><strong>Discharge Type:</strong> ${formData.dischargeType}</span>` : ""}
            ${formData.condition && formData.condition !== "-" ? `<span><strong>Condition:</strong> ${formData.condition}</span>` : ""}
            ${formData.nextAppointment && formData.nextAppointment !== "-" ? `<span style="text-align: right;"><strong>Next Appointment:</strong> ${formData.nextAppointment}</span>` : ""}
          </div>

          <div class="row" style="gap: 15px; align-items: flex-start; margin-top: 10px;">
            ${formData.pho && formData.pho !== "-" ? `
              <div style="flex: 1;">
                <div class="label">Personal History of:</div>
                <div class="box">${formData.pho}</div>
              </div>` : ""}
            ${formData.co && formData.co !== "-" ? `
              <div style="flex: 1;">
                <div class="label">Complaint of:</div>
                <div class="box">${formData.co}</div>
              </div>` : ""}
          </div>

          <div class="row" style="gap: 15px; align-items: flex-start;">
            ${formData.kco && formData.kco !== "-" ? `
              <div style="flex: 1;">
                <div class="label">Known Case of:</div>
                <div class="box">${formData.kco}</div>
              </div>` : ""}
            ${formData.cf && formData.cf !== "-" ? `
              <div style="flex: 1;">
                <div class="label">Clinical Findings:</div>
                <div class="box">${formData.cf}</div>
              </div>` : ""}
          </div>

          ${formData.drWrittenDischargeSummery ? `
          <div class="summary-section">
            <span class="summary-label">CLINICAL SUMMARY & TREATMENT:</span>
            <div class="summary-content">${formData.drWrittenDischargeSummery}</div>
          </div>
          ` : ""}

          <div class="footer">
            <div class="sign-box">
               <div class="sign-line">Patient / Guardian Signature</div>
            </div>
            <div class="sign-box">
              <div class="sign-line">Authority Sign / Doctor Sign</div>
            ${(() => {
      const userRole = authCtx?.role;
      const userName = authCtx?.userName;
      if (userRole === "ROLE_DOCTOR" && userName) {
        return `<div style="font-size: 11px; margin-top: 4px;"><b>( ${userName} )</b></div>`;
      }
      return "";
    })()}
          </div>
        </div>
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 500);
};

export const exportDischargePDF = (formData, authCtx) => {
  const hospitalData = authCtx?.hospitalData;
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4"
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;

  // Use Standardized Report Header
  const startY = drawReportHeader(doc, hospitalData, "DISCHARGE SUMMARY", {
    hideSeparator: true,
    isExport: true,
    margin: 0,
    top: 0
  });

  // Re-define primaryRgb for section headers (fixing ESLint error)
  const themeName = hospitalData?.applicationThemeColour || 'TEAL';
  const theme = themeColors[themeName] || themeColors.TEAL;
  const hexToRgb = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
  };
  const primaryRgb = hexToRgb(theme.primary);

  let currentY = startY + 2;
  doc.setDrawColor(200, 200, 200); // Light gray
  doc.setLineWidth(0.2);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 6;

  doc.setFontSize(11).setFont("helvetica", "normal");
  const addInfoRow = (l1, v1, l2, v2, l3, v3) => {
    const hasValue = (val) => val && val !== "-" && val !== "null" && val !== "undefined";

    if (hasValue(v1)) {
        doc.setFont("helvetica", "bold");
        doc.text(l1, margin + 2, currentY);
        doc.setFont("helvetica", "normal");
        doc.text(String(v1), margin + 32, currentY);
    }

    if (l2 && hasValue(v2)) {
      doc.setFont("helvetica", "bold");
      doc.text(l2, pageWidth * 0.45, currentY);
      doc.setFont("helvetica", "normal");
      doc.text(String(v2), pageWidth * 0.60, currentY);
    }
    if (l3 && hasValue(v3)) {
      doc.setFont("helvetica", "bold");
      doc.text(l3, pageWidth * 0.75, currentY);
      doc.setFont("helvetica", "normal");
      doc.text(String(v3), pageWidth * 0.88, currentY);
    }
    
    if (hasValue(v1) || hasValue(v2) || hasValue(v3)) {
        currentY += 6;
    }
  };

  addInfoRow("Patient Name:", formData.patientName, "Reg. No:", formData.regNo, "IPD No:", formData.ipdNo);
  addInfoRow("Age/Sex:", `${formData.age || "-"}/${formData.sex || "-"}`, "Weight:", (formData.weight || "-") + " kg", "Date:", formData.dod || new Date().toLocaleDateString());
  addInfoRow("DOA:", `${formData.doa || "-"} ${formData.doaTime || ""}`, "DOD:", `${formData.dod || "-"} ${formData.dodTime || ""}`, "Disch Type:", formData.dischargeType);


  currentY += 1;
  if (formData.address && formData.address !== "-") {
    doc.setFont("helvetica", "bold");
    doc.text("Address:", margin + 2, currentY);
    doc.setFont("helvetica", "normal");
    const splitAddr = doc.splitTextToSize(formData.address, pageWidth - margin * 2 - 40);
    doc.text(splitAddr, margin + 32, currentY);
    currentY += (splitAddr.length * 5) + 4;
  }

  // Side-by-Side Diagnosis and Medico-Legal
  const hasDiagnostic = formData.medicoLegal && formData.medicoLegal !== "-";
  const hasDiagnosisVal = formData.diagnosis && formData.diagnosis !== "-";

  if (hasDiagnostic || hasDiagnosisVal) {
    const boxWidth = (pageWidth - margin * 2 - 5) / 2;
    const splitMedico = doc.splitTextToSize(formData.medicoLegal || "-", boxWidth - 4);
    const splitDiagnosis = doc.splitTextToSize(formData.diagnosis || "-", boxWidth - 4);
    const boxHeight1 = Math.max(splitMedico.length, splitDiagnosis.length) * 5 + 6;

    if (hasDiagnostic) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin, currentY, boxWidth, 6, 'F');
        doc.setFont("helvetica", "bold").setFontSize(10);
        doc.text("MEDICO-LEGAL DETAIL", margin + 2, currentY + 4.5);
        doc.setDrawColor(200, 200, 200); // Light gray border
        doc.rect(margin, currentY + 6, boxWidth, boxHeight1);
        doc.setFont("helvetica", "normal").setFontSize(10);
        doc.text(splitMedico, margin + 2, currentY + 11);
    }

    if (hasDiagnosisVal) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin + boxWidth + 5, currentY, boxWidth, 6, 'F');
        doc.setFont("helvetica", "bold");
        doc.text("DIAGNOSIS", margin + boxWidth + 7, currentY + 4.5);
        doc.setDrawColor(200, 200, 200); // Light gray border
        doc.rect(margin + boxWidth + 5, currentY + 6, boxWidth, boxHeight1);
        doc.setFont("helvetica", "normal");
        doc.text(splitDiagnosis, margin + boxWidth + 7, currentY + 11);
    }
    currentY += boxHeight1 + 10;
  }

  if (currentY > pageHeight - 30) { doc.addPage(); currentY = margin + 10; }
  addInfoRow("Condition:", formData.condition, "Next Appt:", formData.nextAppointment);
  currentY += 4;

  const bWidth = (pageWidth - margin * 2 - 5) / 2;

  // New Rows for PHO, CO, KCO, CF after Discharge Info
  if (formData.pho || formData.co) {
    if (currentY > pageHeight - 30) { doc.addPage(); currentY = margin + 10; }
    const splitPho = doc.splitTextToSize(formData.pho || "-", bWidth - 4);
    const splitCo = doc.splitTextToSize(formData.co || "-", bWidth - 4);
    const boxHeight2 = Math.max(splitPho.length, splitCo.length) * 5 + 6;

    if (formData.pho) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin, currentY, bWidth, 6, 'F');
        doc.setFont("helvetica", "bold").setFontSize(10);
        doc.text("PERSONAL HISTORY OF", margin + 2, currentY + 4.5);
        doc.setDrawColor(200, 200, 200); // Light gray border
        doc.rect(margin, currentY + 6, bWidth, boxHeight2);
        doc.setFont("helvetica", "normal").setFontSize(10);
        doc.text(splitPho, margin + 2, currentY + 11);
    }

    if (formData.co) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin + bWidth + 5, currentY, bWidth, 6, 'F');
        doc.setFont("helvetica", "bold");
        doc.text("COMPLAINT OF", margin + bWidth + 7, currentY + 4.5);
        doc.setDrawColor(200, 200, 200); // Light gray border
        doc.rect(margin + bWidth + 5, currentY + 6, bWidth, boxHeight2);
        doc.setFont("helvetica", "normal");
        doc.text(splitCo, margin + bWidth + 7, currentY + 11);
    }
    currentY += boxHeight2 + 10;
  }

  if (formData.kco || formData.cf) {
    if (currentY > pageHeight - 30) { doc.addPage(); currentY = margin + 10; }
    const splitKco = doc.splitTextToSize(formData.kco || "-", bWidth - 4);
    const splitCf = doc.splitTextToSize(formData.cf || "-", bWidth - 4);
    const boxHeight3 = Math.max(splitKco.length, splitCf.length) * 5 + 6;

    if (formData.kco) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin, currentY, bWidth, 6, 'F');
        doc.setFont("helvetica", "bold").setFontSize(10);
        doc.text("KNOWN CASE OF", margin + 2, currentY + 4.5);
        doc.setDrawColor(200, 200, 200); // Light gray border
        doc.rect(margin, currentY + 6, bWidth, boxHeight3);
        doc.setFont("helvetica", "normal").setFontSize(10);
        doc.text(splitKco, margin + 2, currentY + 11);
    }

    if (formData.cf) {
        doc.setFillColor(245, 247, 250);
        doc.rect(margin + bWidth + 5, currentY, bWidth, 6, 'F');
        doc.setFont("helvetica", "bold");
        doc.text("CLINICAL FINDINGS", margin + bWidth + 7, currentY + 4.5);
        doc.setDrawColor(200, 200, 200); // Light gray border
        doc.rect(margin + bWidth + 5, currentY + 6, bWidth, boxHeight3);
        doc.setFont("helvetica", "normal");
        doc.text(splitCf, margin + bWidth + 7, currentY + 11);
    }
    currentY += boxHeight3 + 10;
  }

  if (formData.drWrittenDischargeSummery) {
    if (currentY > pageHeight - 40) { doc.addPage(); currentY = margin + 10; }
    doc.setDrawColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    doc.line(margin, currentY, pageWidth - margin, currentY);
    currentY += 6;
    doc.setFont("helvetica", "bold").setFontSize(11).setTextColor(primaryRgb[0], primaryRgb[1], primaryRgb[2]);
    doc.text("CLINICAL SUMMARY & TREATMENT", margin, currentY);
    currentY += 8;
    doc.setTextColor(0, 0, 0);

    // Advanced HTML Parser for PDF to preserve formatting
    const parseRichText = (html) => {
      // 1. Remove Quill-specific attributes like class="ql-..."
      let cleanHtml = html.replace(/ class="[^"]*"/g, '');
      // 2. Normalize paragraph tags
      cleanHtml = cleanHtml.replace(/<p>/g, '').replace(/<\/p>/g, '\n');
      // 3. Normalize breaks
      cleanHtml = cleanHtml.replace(/<br\s*\/?>/g, '\n');

      const parts = [];
      // Regex to find bold/headings or plain text
      // Supports <b>, <strong>, <h1>-<h6>
      const regex = /<(strong|b|h[1-6])>(.*?)<\/\1>|([^<]+)/gi;
      let match;

      while ((match = regex.exec(cleanHtml)) !== null) {
        if (match[1]) {
          // Bold or Heading part
          const isHeading = match[1].toLowerCase().startsWith('h');
          parts.push({
            text: match[2].replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&'),
            isBold: true,
            isHeading: isHeading
          });
        } else if (match[3]) {
          // Normal text
          parts.push({
            text: match[3].replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&'),
            isBold: false
          });
        }
      }
      return parts;
    };

    const richParts = parseRichText(formData.drWrittenDischargeSummery);

    doc.setFontSize(10);
    richParts.forEach(part => {
      doc.setFont("helvetica", part.isBold ? "bold" : "normal");
      if (part.isHeading) doc.setFontSize(12);
      else doc.setFontSize(10);

      const lines = doc.splitTextToSize(part.text, pageWidth - margin * 2);
      lines.forEach(line => {
        if (currentY > pageHeight - 20) { doc.addPage(); currentY = margin + 10; }
        // Only draw if line is not just whitespace from tag stripping
        if (line.trim()) {
          doc.text(line, margin, currentY);
          currentY += 5;
        }
      });
      if (part.isHeading) currentY += 2;
    });
    currentY += 8;
  }

  // Signatures
  let footerY = pageHeight - 25;
  if (currentY > footerY - 15) {
    doc.addPage();
    footerY = pageHeight - 25;
  }

  doc.setDrawColor(0, 0, 0).setLineWidth(0.3);
  doc.line(margin, footerY, margin + 50, footerY);
  doc.text("Patient / Guardian Signature", margin + 25, footerY + 5, { align: "center" });

  doc.line(pageWidth - margin - 50, footerY, pageWidth - margin, footerY);
  doc.setFont("helvetica", "bold").setFontSize(9);
  doc.text("Authority Sign / Doctor Sign", pageWidth - margin - 25, footerY + 5, { align: "center" });

  const userRole = authCtx?.role;
  const userName = authCtx?.userName;
  if (userRole === "ROLE_DOCTOR" && userName) {
    doc.setFontSize(8);
    doc.text(`( ${userName} )`, pageWidth - margin - 25, footerY + 10, { align: "center" });
  }

  doc.save(`${(formData.patientName || "patient")}_Discharge.pdf`);
};

const DPrint = ({ formData, hospitalData, paperSize = "a4" }) => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  return (
    <button onClick={() => printDischarge(formData, authCtx, paperSize)} className="btn-premium">
      <FaPrint className="me-2" /> {t("Print")}
    </button>
  );
};

export default DPrint;
